import { useEffect, useRef } from 'react';

import { realtimeSubscribe, type RealtimeChannel, type RealtimeHandler } from '@/lib/realtime';

/**
 * Subscribe to a realtime channel for the lifetime of the component. The handler is
 * held in a ref so re-renders don't churn the subscription; pass `enabled: false` to
 * pause (e.g. while a detail screen is unmounted). Backed by the installed transport
 * (mock bus under VITE_MOCK_API, socket.io at go-live).
 */
export function useRealtimeChannel<T = unknown>(
  channel: RealtimeChannel,
  handler: RealtimeHandler<T>,
  enabled = true,
): void {
  const ref = useRef(handler);
  // Keep the latest handler without re-subscribing on every render.
  useEffect(() => {
    ref.current = handler;
  });

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = realtimeSubscribe<T>(channel, (payload) => ref.current(payload));
    return unsubscribe;
  }, [channel, enabled]);
}
